// page-meeting-cr.ts — Onglet « Génération CR » de la fiche réunion :
// fermeture de la réunion, liste des participants ayant pris des notes
// (aperçu, inclusion), génération du compte rendu.
//
// Troisième tranche de l'extraction des closures de `renderMeetingPage`
// (audit dette technique 2026-09-07, point 16). Reprend la mécanique de
// l'ancienne modale openCRModal de page-meetings-records.ts :
//   - sélecteur de fichier support (xlsx/docx/pptx/xls)
//   - liste des participants ayant pris des notes, avec checkbox d'inclusion
//   - bouton "Générer" qui POST /generate-cr et déclenche le download.
// L'endpoint exige déjà que l'utilisateur soit animateur ; l'onglet n'est
// visible que dans ce cas.
//
// État explicite (`CrTabContext`) : l'id de la réunion, le droit
// `meetings.analyze` (verrou palier sur la génération IA) et le rappel à
// jouer après fermeture, que la page utilise pour se re-rendre en lecture
// seule. Le markup de l'onglet (`renderCrTab`) reste dans la page.

import { sanitizeRichText } from '../utils/rich-text.js';

export interface CrNoteEntry {
  user_id: number;
  trigram: string;
  name: string;
  content: string;
}

export interface CrTabContext {
  meetingId: number;
  /** Droit `meetings.analyze` : sans lui, « Générer » est cadenassé. */
  canAnalyze: boolean;
  /** Appelé après une fermeture réussie (la page relance son rendu). */
  onClosed: () => void;
}

export interface CrTab {
  wire(host: HTMLElement): void;
}

export const CR_PREVIEW_OVERLAY_ID = 'mp-cr-note-preview-overlay';

export function openCrNotePreview(note: CrNoteEntry): void {
  const existing = document.getElementById(CR_PREVIEW_OVERLAY_ID);
  if (existing) existing.remove();
  const overlay = document.createElement('div');
  overlay.id = CR_PREVIEW_OVERLAY_ID;
  overlay.style.cssText =
    'position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,0.45);display:flex;align-items:center;justify-content:center';
  const panel = document.createElement('div');
  panel.style.cssText =
    'background:var(--bg);border-radius:var(--radius);padding:20px 24px;max-width:640px;width:90%;max-height:78vh;display:flex;flex-direction:column;gap:10px;box-shadow:0 8px 32px rgba(0,0,0,0.28)';
  const header = document.createElement('div');
  header.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:8px';
  const titleEl = document.createElement('strong');
  titleEl.style.fontSize = '14px';
  titleEl.textContent = t('pageMeetingsRecords.modalCR.previewTitle')
    .replace('{trigram}', note.trigram)
    .replace('{name}', note.name);
  const closeBtn = document.createElement('button');
  closeBtn.textContent = '✕';
  closeBtn.style.cssText =
    'background:none;border:none;cursor:pointer;font-size:18px;color:var(--muted);padding:0 4px';
  closeBtn.addEventListener('click', () => overlay.remove());
  header.append(titleEl, closeBtn);
  const content = document.createElement('div');
  content.style.cssText =
    'overflow-y:auto;flex:1;line-height:1.6;padding:10px 12px;border:1px solid var(--border2);border-radius:var(--radius);min-height:100px;background:var(--bg2);font-size:13px';
  content.innerHTML =
    sanitizeRichText(note.content) ||
    `<span style="color:var(--muted)">${esc(t('pageMeetingsRecords.modalCR.previewEmpty'))}</span>`;
  panel.append(header, content);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });
}

/** Table des participants ayant pris des notes, checkboxes cochées par
 * défaut : l'utilisateur dé-coche pour exclure des notes du CR généré. */
export function crNotesTableHtml(notes: ReadonlyArray<CrNoteEntry>): string {
  if (!notes.length) {
    return `<span style="color:var(--muted);font-size:13px">${esc(t('pageMeetingsRecords.modalCR.notesEmpty'))}</span>`;
  }
  const rowsHtml = notes
    .map(
      (n) => `<tr id="mp-cr-note-row-${n.user_id}">
              <td>
                <span class="badge badge-outline" title="${esc(n.name)}">${esc(n.trigram)}</span>
                <span style="margin-left:6px;font-size:13px">${esc(n.name)}</span>
              </td>
              <td style="text-align:center;width:72px">
                <button type="button" class="btn btn-sm btn-secondary mp-cr-preview-btn"
                  data-note-uid="${n.user_id}" title="${esc(t('pageMeetingsRecords.modalCR.colPreview'))}">👁</button>
              </td>
              <td style="text-align:center;width:80px">
                <input type="checkbox" data-note-uid="${n.user_id}" checked
                  style="width:16px;height:16px;cursor:pointer;accent-color:var(--accent)">
              </td>
            </tr>`,
    )
    .join('');
  return `<table class="table" style="margin-top:4px">
            <thead><tr>
              <th>${esc(t('pageMeetingsRecords.modalCR.colParticipant'))}</th>
              <th style="text-align:center;width:72px">${esc(t('pageMeetingsRecords.modalCR.colPreview'))}</th>
              <th style="text-align:center;width:80px">${esc(t('pageMeetingsRecords.modalCR.colUse'))}</th>
            </tr></thead>
            <tbody>${rowsHtml}</tbody>
          </table>`;
}

export function createCrTab(ctx: CrTabContext): CrTab {
  const { meetingId, canAnalyze, onClosed } = ctx;

  // CTA "Fermer la réunion" : POST puis rappel pour recalculer les états
  // read-only (banners, désactivation des éditeurs, masquage des CTA
  // suppression/modification). Confirme avant pour éviter les fermetures
  // accidentelles — l'opération n'a pas de UI de retour arrière côté front.
  function wireClose(host: HTMLElement): void {
    host.querySelector<HTMLButtonElement>('#mp-meeting-close')?.addEventListener('click', () => {
      if (!confirm(t('pageMeeting.cr.closeConfirm'))) return;
      void (async () => {
        try {
          // La fermeture purge les drafts non publiés côté backend (parité
          // avec le « Fermer » du wizard standalone). On remonte un toast
          // info distinct si des drafts ont été nettoyés, pour que l'animateur
          // n'ait pas l'impression d'avoir « perdu » des saisies.
          const res = (await api.closeMeetingRecord(meetingId)) as {
            purged_drafts?: number;
          } | null;
          toast(t('pageMeeting.cr.toastClosed'), 'ok');
          const purged = res?.purged_drafts || 0;
          if (purged > 0) {
            toast(t('pageMeeting.cr.toastDraftsPurged', { n: purged }), 'info');
          }
          onClosed();
        } catch (err: unknown) {
          toast(err instanceof Error ? err.message : t('common.error'), 'error');
        }
      })();
    });
  }

  // Charge la liste des participants ayant pris des notes.
  async function loadNotes(host: HTMLElement, notesWrap: HTMLElement): Promise<void> {
    try {
      const notes = (await api.getAllMeetingNotes(meetingId)) as CrNoteEntry[];
      notesWrap.innerHTML = crNotesTableHtml(notes);
      notesWrap.querySelectorAll<HTMLInputElement>('input[data-note-uid]').forEach((cb) => {
        cb.addEventListener('change', () => {
          const row = host.querySelector<HTMLElement>(`#mp-cr-note-row-${cb.dataset['noteUid']}`);
          if (row) row.style.opacity = cb.checked ? '1' : '0.35';
        });
      });
      notesWrap.querySelectorAll<HTMLButtonElement>('.mp-cr-preview-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          const uid = parseInt(btn.dataset['noteUid'] || '0');
          const n = notes.find((x) => x.user_id === uid);
          if (n) openCrNotePreview(n);
        });
      });
    } catch (err: unknown) {
      notesWrap.innerHTML = `<span style="color:var(--danger);font-size:13px">${esc(err instanceof Error ? err.message : t('common.error'))}</span>`;
    }
  }

  // Verrou palier : sans meetings.analyze, la génération IA du CR est
  // cadenassée (upsell premium standard) et le clic ne fait rien. Même droit
  // que l'analyse de CR ; l'endpoint POST /generate-cr renvoie 403 sinon.
  function wireGenerate(host: HTMLElement, notesWrap: HTMLElement | null): void {
    const statusEl = host.querySelector<HTMLElement>('#mp-cr-status');
    const crGenBtn = host.querySelector<HTMLButtonElement>('#mp-cr-generate');
    if (crGenBtn && !canAnalyze && window.markLocked) {
      window.markLocked(crGenBtn, 'premium');
    }
    crGenBtn?.addEventListener('click', () => {
      if (!canAnalyze) return;
      const fileInput = host.querySelector<HTMLInputElement>('#mp-cr-file');
      const supportFile = fileInput?.files?.[0] ?? null;
      const includedUserIds: number[] = [];
      notesWrap?.querySelectorAll<HTMLInputElement>('input[data-note-uid]').forEach((cb) => {
        if (cb.checked) includedUserIds.push(parseInt(cb.dataset['noteUid'] || '0'));
      });
      if (includedUserIds.length === 0) {
        toast(t('pageMeetingsRecords.modalCR.noNotesSelected'), 'error');
        return;
      }
      // Overlay d'attente identique à celui du CTA "Analyser CR" — la
      // génération IA peut prendre 15-40 s, on bloque visuellement l'écran
      // pour signaler le travail en cours. On désactive aussi le bouton
      // (l'overlay ne garantit pas contre un re-déclenchement clavier sur le
      // bouton encore focalisé → double génération IA + double coût API).
      crGenBtn.disabled = true;
      if (statusEl) statusEl.textContent = t('pageMeetingsRecords.modalCR.generating');
      const overlay = window.componentCrAnalysis.showAnalyseOverlay(
        t('pageMeetingsRecords.modalCR.generating'),
        t('pageMeetingsRecords.modalCR.title'),
      );
      void (async () => {
        try {
          await api.generateMeetingCR(meetingId, supportFile, includedUserIds);
          if (statusEl) statusEl.textContent = '';
          overlay.remove();
          toast(t('pageMeetingsRecords.modalCR.done'), 'ok');
        } catch (err: unknown) {
          if (statusEl) statusEl.textContent = '';
          overlay.remove();
          toast(err instanceof Error ? err.message : t('common.error'), 'error');
        } finally {
          crGenBtn.disabled = false;
        }
      })();
    });
  }

  function wire(host: HTMLElement): void {
    const notesWrap = host.querySelector<HTMLElement>('#mp-cr-notes-wrap');
    wireClose(host);
    if (notesWrap) void loadNotes(host, notesWrap);
    wireGenerate(host, notesWrap);
  }

  return { wire };
}
